import React, { useState } from "react";
import { BaseDialog } from "components/common/Dialogs";
import { TextField } from "components/common/Form";
import { Error } from "components/common/ControlElems";
import { getApiErrorMessage } from "context/helpers/contentContextHelper";
import styles from "./ReturningMembers.module.scss";

function isValidEmail(value) {
    const email = String(value || "").trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function SendOfferDialog({
    member,
    processing,
    result,
    error,
    onClose,
    onSend
}) {
    const defaultEmail = String(member.email || "").trim();
    const [email, setEmail] = useState(defaultEmail);
    const [confirmed, setConfirmed] = useState(false);
    const [validationError, setValidationError] = useState("");

    const displayName =
        member.fullName || member.login || member.email || `User #${member.id}`;
    const trimmedEmail = email.trim();
    const emailChanged =
        trimmedEmail.toLowerCase() !== defaultEmail.toLowerCase();

    const handleEmailChange = event => {
        setEmail(event.target.value);
        setConfirmed(false);
        setValidationError("");
    };

    const handleSendClick = () => {
        if (!isValidEmail(trimmedEmail)) {
            setValidationError("Enter a valid email address before sending.");
            setConfirmed(false);
            return;
        }
        if (emailChanged && !confirmed) {
            setConfirmed(true);
            return;
        }
        onSend({
            userId: member.id,
            email: trimmedEmail
        });
    };

    return (
        <BaseDialog
            id="sendReturningMemberOfferDialog"
            label="Send Returning Member Offer"
            onClose={onClose}
        >
            <div className={styles["send-offer-dialog"]}>
                <p className={styles["send-offer-target"]}>
                    Send offer for <strong>{displayName}</strong>
                    {member.email ? ` (account: ${member.email})` : ""}
                </p>

                {!result && (
                    <>
                        <TextField
                            value={email}
                            name="offerEmail"
                            label="Email"
                            type="email"
                            placeholder="Enter email address"
                            disabled={processing}
                            required
                            onChange={handleEmailChange}
                        />
                        {validationError && <Error text={validationError} />}
                        {confirmed && emailChanged && (
                            <p className={styles["send-offer-confirm"]}>
                                Email changed from{" "}
                                <strong>{defaultEmail || "none"}</strong> to{" "}
                                <strong>{trimmedEmail}</strong>. Confirm send
                                to the new address?
                            </p>
                        )}
                    </>
                )}

                {error && (
                    <Error
                        text={getApiErrorMessage(
                            error,
                            "Failed to send offer. DJ may not be eligible, or email delivery failed."
                        )}
                    />
                )}

                {result && (
                    <div className={styles["send-offer-result"]}>
                        <p className={styles["send-offer-success"]}>
                            {result.message || "Offer sent successfully."}
                        </p>
                        {result.offerLink && (
                            <p>
                                <span className={styles["result-label"]}>
                                    Offer link:
                                </span>{" "}
                                <a
                                    href={result.offerLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {result.offerLink}
                                </a>
                            </p>
                        )}
                        {result.expiresAt && (
                            <p>
                                <span className={styles["result-label"]}>
                                    Expires:
                                </span>{" "}
                                {result.expiresAt}
                            </p>
                        )}
                    </div>
                )}

                <div className="d-flex flex-row justify-content-between mt-3">
                    <button
                        type="button"
                        className="btn btn-secondary w-25"
                        onClick={onClose}
                        disabled={processing}
                    >
                        {result ? "Close" : "Cancel"}
                    </button>
                    {!result && (
                        <button
                            type="button"
                            className="btn btn-primary w-50"
                            onClick={handleSendClick}
                            disabled={processing || !trimmedEmail}
                        >
                            {processing
                                ? "Sending..."
                                : confirmed && emailChanged
                                ? "Confirm Send"
                                : "Send Offer"}
                        </button>
                    )}
                </div>
            </div>
        </BaseDialog>
    );
}
